/**
 * Checkout enhancements for Klarna support.
 *
 * @classdesc Klarna Checkout
 */
const kp = {
  klarnaPaymentsUrls: window.KlarnaPaymentsUrls,
  klarnaPaymentsObjects: window.KlarnaPaymentsObjects,
  klarnaPaymentsConstants: window.KPConstants,
  klarnaPaymentsPreferences: window.KPPreferences,

  getCustomerEmail: () => {
    const contactInfoBlock = $('.contact-info-block');
    const customerInformationBlock = $('.customer-information-block');
    const emailFromOrderSummary = $('.order-summary-email').text();
    let email;

    if (emailFromOrderSummary.length) {
      email = emailFromOrderSummary;
    } else {
      email = contactInfoBlock.find('.email').length
        ? contactInfoBlock.find('.email').val() : customerInformationBlock.find('.email').val();
    }

    return email;
  },

  getPhoneFromAddressBlock: (addressBlock, prefixSelector, phoneSelector) => {
    const phonePrefix = addressBlock.find(prefixSelector).val();
    const phone = addressBlock.find(phoneSelector).val();
    return phonePrefix + phone;
  },

  getSelectedPickupPoint: () => JSON.parse(
    $('#store-locator-search-results .result.is-selected input')
      .attr('data-store-info')) || {}
  ,

  buildBillingAddressData: () => {
    const billingAddressFieldset = $('#dwfrm_billing .billing-address-block');
    const contactInfoBlock = $('.contact-info-block');
    const address = {
      email: kp.getCustomerEmail(),
      phone: kp.getPhoneFromAddressBlock(
        contactInfoBlock, '#billing-phone-prefix', '#phoneNumber'
      ),
    };

    address.given_name = billingAddressFieldset.find('#billingFirstName').val() ?? '';
    address.family_name = billingAddressFieldset.find('#billingLastName').val() ?? '';
    address.street_address = billingAddressFieldset.find('#billingAddressOne').val() ?? '';
    address.street_address2 = billingAddressFieldset.find('#billingAddressTwo').val() ?? '';
    address.city = billingAddressFieldset.find('#billingAddressCity').val() ?? '';
    address.region = billingAddressFieldset.find('#billingState').val() ?? '';
    address.postal_code = billingAddressFieldset.find('#billingZipCode').val() ?? '';
    address.country = billingAddressFieldset.find('#billingCountry').val() ?? '';

    return address;
  },

  userHasEnteredShippingAddress: () => {
    const $shipmentUUIDElement = $('.single-shipping .shipping-form')
      .find('input[name="shipmentUUID"]');
    return $shipmentUUIDElement.length > 0 && $shipmentUUIDElement[0].value !== ''
      && $shipmentUUIDElement[0].value !== undefined;
  },

  buildShippingAddressData: (billingAddress) => {
    const address = {};

    // if picking
    if (billingAddress) {
      const pickupPoint = kp.getSelectedPickupPoint();
      address.given_name = billingAddress?.given_name ?? ''; // data from billing address
      address.family_name = billingAddress?.family_name ?? '';
      address.email = billingAddress.email ?? '';
      address.phone = billingAddress.phone ?? '';
      address.street_address = pickupPoint.address1 ?? ''; // data from store/droppoint
      address.street_address2 = '';
      address.city = pickupPoint.city ?? '';
      address.postal_code = pickupPoint.postalCode ?? '';
      address.region = pickupPoint.stateCode ?? '';
      address.country = pickupPoint.countryCode ?? '';
    } else {
      const addressBlock = $('.single-shipping .shipping-address-block');
      address.given_name = addressBlock.find('.shippingFirstName').val();
      address.family_name = addressBlock.find('.shippingLastName').val();
      address.street_address = addressBlock.find('.shippingAddressOne').val();
      address.street_address2 = addressBlock.find('.shippingAddressTwo').val();
      address.city = addressBlock.find('.shippingAddressCity').val();
      address.postal_code = addressBlock.find('.shippingZipCode').val();
      address.region = addressBlock.find('.shippingState').val();
      address.country = addressBlock.find('.shippingCountry').val();
      address.email = addressBlock.find('#shippingEmail').val();
      address.phone = kp.getPhoneFromAddressBlock(
        addressBlock, '#shipping-phone-prefix', '.shippingPhoneNumber',
      );
    }

    return address;
  },

  init: () => {
    try {
      Klarna.Payments.init({
        client_token: kp.klarnaPaymentsObjects.clientToken,
      });
      Klarna.Payments.load({
        container: '#klarna-container',
        payment_method_category: 'klarna',
      }, (res) => {
        if (res.show_form === false) {
          kp.removeKlarnaFromCheckout();
        }
        if (!res || res.error) {
          kp.writeAdditionalLog(res, 'Klarna.Payments.load()', 'SFRA Storefront Ajax Request Error.');
        }
      });
    } catch (e) {
      kp.removeKlarnaFromCheckout();
    }
  },

  buildAuthorizeRequestData: () => {
    const klarnaRequestData = {
      billing_address: kp.buildBillingAddressData(),
    };

    klarnaRequestData.shipping_address = kp.buildShippingAddressData(
      kp.isPicking() ? klarnaRequestData.billing_address : null,
    );

    if (window.KPCustomerInfo && window.KPCustomerInfo.attachment) {
      klarnaRequestData.attachment = kp.buildAttachmentData(klarnaRequestData.billing_address);
    }

    return klarnaRequestData;
  },

  /**
   * Authorize a payment
   *
   * @example
   * 1. Success response: The purchase is approved.
   *   { approved: true }
   * 2. Fixable error response: The customer needs to adjust and try again.
   *   { approved: false, error: true }
   * 3. Error response: The purchase is not approved and you can't display Klarna
   *   as a payment option.
   *   { approved: false, error: true }
   * 4. Back to form: Canceled auth (popup closed)
   *   { approved: false, error: false }
   * @returns {Promise.<Object.<string, boolean>>} Status of the auth
   * ({ approved, error})
   * @see https://docs.klarna.com/klarna-payments/integrate-with-klarna-payments/step-2-check-out/22-get-authorization/
   */
  authorize: () => new Promise((resolve, reject) => {
    try {
      Klarna.Payments.authorize({
        payment_method_category: 'klarna',
        auto_finalize: true,
      }, kp.buildAuthorizeRequestData(), (res) => {
        if (res.approved) {
          // 1. Success response
          $.ajax({
            headers: {
              'X-Auth': res.authorization_token,
              'Finalize-Required': res.finalize_required,
            },
            url: kp.klarnaPaymentsUrls.saveAuth,
          }).done(() => {
            resolve({
              approved: true,
              error: false
            });
          }).fail(() => {
            reject(new Error('Failed when saving auth'));
          });
        } else if (res.show_form) {
          if (res.error) {
            kp.writeAdditionalLog(res, 'Klarna.Payments.authorize()', 'SFRA Storefront Ajax Request Error.');
            // 2. Fixable error response
            resolve({
              approved: false,
              error: true
            });
          } else {
            // 4. Back to form
            /*
            When canceling Klarna by means of closing the pop-up, the res can vary
            * Using browsers native close button:
              res => {show_form: true, approved: false}
            * Using Klarna custom close button:
              res => {show_form: true, approved: false, finalize_required: false}

            But the flag 'show_form' is always active. So, we can suppose that
            this condition is the cancelation. By the way, the response seems
            quite generic so we will resolve as non-authorized status without
            known error.
            */
            resolve({
              approved: false,
              error: false,
            });
          }
        } else if (!res.show_form) {
          // 3. Error response
          reject(new Error('Your store is not able to offer Klarna as a payment option'));
        } else {
          kp.writeAdditionalLog(res, 'Klarna.Payments.authorize()',
            'SFRA Storefront Ajax Request Error (Unknown response).'
          );
          reject(new Error('Unknown response'));
        }
      });
    } catch (e) {
      kp.writeAdditionalLog(e, 'Klarna.Payments.authorize()',
        'SFRA Storefront Ajax Request Error (Unknown error).'
      );
      reject(new Error('Unknown error', { cause: e }));
    }
  }),

  isPicking: () => {
    const shippingMethodsConfig = require(
      '../../../../../../../app_byl_core/cartridges/app_byl_core/cartridge/config/shippingMethodsConfig.json', // eslint-disable-line n/no-unpublished-require
    );
    const shippingForm = $('.shipping-form');
    const checkedShippingMethod = shippingForm.find('.shipping-block__input:checked').val();

    switch (checkedShippingMethod) {
      case shippingMethodsConfig.id.DROPPOINT:
        return kp.klarnaPaymentsConstants.SHIPPING_METHOD_TYPE.POINT;
      case shippingMethodsConfig.id.INSTOREPICKUP:
        return kp.klarnaPaymentsConstants.SHIPPING_METHOD_TYPE.STORE;
      case shippingMethodsConfig.id.CCEXPRESS:
        return kp.klarnaPaymentsConstants.SHIPPING_METHOD_TYPE.STORE;
      default:
        return false;
    }
  },

  buildOtherDeliveryAddressData: (billingAddress) => {
    const addresses = [];
    const isPicking = kp.isPicking();

    if (isPicking) {
      const pickupPoint = kp.getSelectedPickupPoint();
      addresses.push({
        shipping_method: isPicking,
        shipping_type: kp.klarnaPaymentsConstants.SHIPPING_TYPE.NORMAL,
        first_name: billingAddress?.given_name ?? '',
        last_name: billingAddress?.family_name ?? '',
        street_address: pickupPoint.address1 ?? '',
        postal_code: pickupPoint.postalCode ?? '',
        city: pickupPoint.city ?? '',
        country: pickupPoint.countryCode ?? '',
      });
    }

    return addresses;
  },

  buildAttachmentData: (billingAddress) => {
    const kpAttachment = window.KPCustomerInfo.attachment;
    const kpAttachmentBody = JSON.parse(kpAttachment.body);
    const isPicking = kp.isPicking();
    if (isPicking) {
      kpAttachmentBody.other_delivery_address = kp.buildOtherDeliveryAddressData(billingAddress);
    } else if (Object.prototype.hasOwnProperty.call(kpAttachmentBody, "other_delivery_address")) {
      delete kpAttachmentBody.other_delivery_address;
    }
    kpAttachment.body = JSON.stringify(kpAttachmentBody);
    return kpAttachment;
  },

  removeKlarnaFromCheckout: () => {
    $('#klarna-nav, #klarna-content').remove();
  },

  writeAdditionalLog: (res, action, msg) => {
    if (kp.klarnaPaymentsPreferences.kpAdditionalLogging) {
      $.ajax({
        url: kp.klarnaPaymentsUrls.writeLog,
        method: 'POST',
        data: {
          response: res,
          actionName: action,
          message: msg
        }
      })
    }
  },

};

/**
 * Initialize Klarna checkout enhancements.
 *
 * This method is called as soon as the Klarna JS API client has been
 * properly initialized (this is done by the client itself).
 */
window.klarnaAsyncCallback = () => {
  kp.init();
};

module.exports = {
  authorize: kp.authorize,
  init: kp.init
};
